import { countBy, sumBy } from 'lodash'
import { useCallback, useMemo } from 'react'
import { ethAddress, getAddress } from 'viem'
import { LEND_V1_DEPRECATION_DATE } from '@/llamalend/constants'
import {
  calculateLendMarketTvlUsd,
  calculateMarketSolvency,
  calculateMintMarketTvlUsd,
  createGetBadDebtMarket,
  lowSolvencyDeprecatedMessage,
} from '@/llamalend/llama.utils'
import {
  computeTotalRate,
  getMarketCampaigns,
  getSupplyApyMetrics,
  sumCampaignsApr,
  sumCampaignsApy,
} from '@/llamalend/rates.utils'
import { type Chain } from '@curvefi/prices-api'
import { crvUsdPages, lendPages } from '@evm-ui/shared/routes'
import { type ExtraIncentive, MarketType, MarketVersion, MarketRateType } from '@evm-ui/types/market'
import type { Address } from '@primitives/address.utils'
import type { Decimal } from '@primitives/decimal.utils'
import { assert } from '@primitives/objects.utils'
import type { QueriesResults } from '@tanstack/react-query'
import { getCampaignsExternalOptions } from '@ui/features/campaigns/campaigns-external.query'
import { getCampaignsMarketsMerklOptions } from '@ui/features/campaigns/campaigns-markets-merkl.query'
import { combineCampaigns } from '@ui/features/campaigns/campaigns.query'
import type { CampaignRewards } from '@ui/features/campaigns/types'
import { combineQueryState } from '@ui/features/queries/combine'
import { useQueries } from '@ui/features/queries/query-hooks'
import { DISABLED_Q, type Query } from '@ui/features/queries/util'
import { decimal, decimalDiv } from '@ui/lib/decimal'
import { aprToApy } from '@ui/lib/rates.utils'
import { DEPRECATED_LLAMAS, NO_LEVERAGE_LEND } from '../../markets.constants'
import { getBadDebtLendMarketsOptions, getBadDebtMintMarketsOptions } from '../market/market-bad-debt.query'
import { getFavoriteMarketOptions } from './favorite-markets'
import { getLendingVaultsOptions, type LendingPosition, LendingVault } from './lending-vaults'
import { type UserHasPosition, type UserHasPositions, useUserLlamaPositions } from './llama-market-positions'
import { getMintMarketOptions, MintMarket } from './mint-markets'

type Assets = { borrowed: AssetDetails; collateral: AssetDetails }

export type AssetDetails = {
  symbol: string
  address: Address
  decimals: number
  blockchainId: Chain
  balance: number | null
  balanceUsd: number | null
  rebasingYield: number | null
  rebasingYieldApr: number | null
}

export type LlamaMarket = {
  blockchainId: Chain
  ammAddress: Address
  controllerAddress: Address
  vaultAddress: Address | null
  supplyGaugeAddress: Address | null
  assets: Assets
  version: MarketVersion
  minBand?: number
  maxBand?: number
  maxLtv: number | null
  loans: number
  oraclePrice?: number
  ammPrice: number
  monetaryPolicyAddress?: Address
  oracleAddress?: Address
  parameters: { A: number | null; loanDiscount: Decimal; liquidationDiscount: Decimal; adminFee: Decimal }
  utilizationPercent: number
  liquidity: number
  liquidityUsd: number
  tvl: number
  totalDebtUsd: number
  totalCollateralUsd: number
  solvencyPercent: number | null
  badDebtUsd: number | undefined
  debtCeiling: number | null // only for mint markets, null for lend markets
  rates: {
    lendApy: number | null // base lend APY %
    lendCrvAprUnboosted: number | null
    lendCrvAprBoosted: number | null
    lendTotalApyMinBoosted: number | null
    lendTotalApyMaxBoosted: number | null // supply rate + rebasing yield + total extra incentives + max boosted yield
    borrowApy: number // base borrow APY %
    borrowTotalApy: number // borrow APY - yield from collateral
    borrowApr: number
    borrowTotalApr: number // borrow APR - yield from collateral
    // extra lending incentives, like OP rewards (so non CRV)
    incentives: ExtraIncentive[]
  }
  lendingPosition?: LendingPosition
  type: MarketType
  url: string
  rewards: CampaignRewards[]
  isFavorite: boolean
  leverage: number | null
  deprecatedMessage: string | null
  userHasPositions: UserHasPosition | null // null means no positions in either market and makes easy to filter
  createdAt: number
  favoriteKey: Address // this differs per market type; for lend markets the vault address, for mint markets the amm address
}

export type LlamaMarketsResult = {
  markets: LlamaMarket[]
  userHasPositions: UserHasPositions | null
  hasFavorites: boolean
}

const toMarketVersion = (version: number): MarketVersion =>
  assert({ 1: MarketVersion.v1, 2: MarketVersion.v2 }[version], `Unsupported LlamaLend market version: ${version}`)

/** Converts API 1e18-scaled discount fractions to UI percent units (div by 1e18*100). */
const scaledFractionToPercent = (value: number): Decimal =>
  decimalDiv(assert(decimal(value), `Invalid scaled discount: ${value}`), '10000000000000000')

const convertLendingVault = (
  {
    controller,
    chain: blockchainId,
    totalAssets,
    totalAssetsUsd,
    totalDebt,
    totalDebtUsd,
    vault,
    gaugeAddress: supplyGaugeAddress,
    llamma,
    collateralToken,
    borrowedToken,
    borrowedBalanceUsd,
    collateralBalanceUsd,
    borrowApr,
    apyLend: lendApy,
    aprLendCrv0Boost: lendCrvAprUnboosted,
    aprLendCrvMaxBoost: lendCrvAprBoosted,
    leverage,
    extraRewardApr,
    maxLtv,
    ammA,
    loanDiscount,
    liquidationDiscount,
    minBand,
    maxBand,
    createdAt,
    version,
    nLoans,
    priceOracle,
    ammPrice,
    policy,
    oracle,
    adminFee,
  }: LendingVault,
  favoriteMarkets: Set<Address>,
  campaigns: Record<string, CampaignRewards[]> = {},
  userBorrows: Set<Address> | undefined,
  lendingPosition: LendingPosition | undefined,
  badDebtUsd?: number,
): LlamaMarket => {
  const marketType = MarketType.Lend
  const borrowApy = aprToApy(borrowApr, 'llamalend.borrow')
  const hasBorrowed = userBorrows?.has(controller) ?? null
  const totalExtraRewardApy =
    // sumBy returns 0 for empty arrays
    extraRewardApr.length ? sumBy(extraRewardApr, reward => aprToApy(reward.rate, 'llamalend.rewards')) : null
  const rewards = getMarketCampaigns(campaigns, {
    marketType,
    controllerAddress: controller,
    vaultAddress: vault,
    ammAddress: llamma,
  })
  const borrowCampaignsApr = sumCampaignsApr(rewards.filter(r => r.action === 'borrow'))
  const borrowCampaignsApy = sumCampaignsApy(rewards.filter(r => r.action === 'borrow'))
  const supplyCampaignsApy = sumCampaignsApy(rewards.filter(r => r.action === 'supply'))
  const { totalMinBoost, totalMaxBoost } = getSupplyApyMetrics({
    supplyApy: lendApy,
    crvBoostApr: [lendCrvAprUnboosted, lendCrvAprBoosted],
    rebasingYieldApy: borrowedToken?.rebasingYield,
    extraIncentivesApy: totalExtraRewardApy,
    campaignsApy: supplyCampaignsApy,
  })
  const solvencyPercent = calculateMarketSolvency({ totalAssetsUsd, badDebtUsd })

  return {
    blockchainId,
    controllerAddress: controller,
    ammAddress: llamma,
    vaultAddress: vault,
    supplyGaugeAddress,
    version: toMarketVersion(version),
    assets: {
      borrowed: { ...borrowedToken, blockchainId, balance: totalDebt, balanceUsd: totalDebtUsd },
      collateral: { ...collateralToken, blockchainId, balance: totalAssets, balanceUsd: totalAssetsUsd },
    },
    maxLtv,
    minBand,
    maxBand,
    loans: nLoans,
    oraclePrice: priceOracle,
    ammPrice,
    monetaryPolicyAddress: policy,
    oracleAddress: oracle,
    parameters: {
      A: ammA,
      loanDiscount: scaledFractionToPercent(loanDiscount),
      liquidationDiscount: scaledFractionToPercent(liquidationDiscount),
      adminFee: scaledFractionToPercent(adminFee),
    },
    utilizationPercent: totalAssetsUsd && (100 * totalDebtUsd) / totalAssetsUsd,
    solvencyPercent,
    badDebtUsd,
    debtCeiling: null, // debt ceiling is not applicable for lend markets
    liquidity: Math.max(0, totalAssets - totalDebt),
    liquidityUsd: Math.max(0, totalAssetsUsd - totalDebtUsd),
    totalDebtUsd,
    totalCollateralUsd: collateralBalanceUsd + borrowedBalanceUsd,
    // TVL = collateral converted to borrow token + collateral + unborrowed supplied assets.
    tvl: calculateLendMarketTvlUsd({ borrowedBalanceUsd, collateralBalanceUsd, totalAssetsUsd, totalDebtUsd }),
    rates: {
      lendApy,
      lendCrvAprUnboosted,
      lendCrvAprBoosted,
      lendTotalApyMinBoosted: totalMinBoost,
      lendTotalApyMaxBoosted: totalMaxBoost,
      borrowApy,
      borrowTotalApy: computeTotalRate(borrowApy, collateralToken.rebasingYield ?? 0, borrowCampaignsApy ?? 0),
      borrowApr,
      borrowTotalApr: computeTotalRate(borrowApr, collateralToken.rebasingYieldApr ?? 0, borrowCampaignsApr ?? 0),
      incentives: extraRewardApr
        ? extraRewardApr.map(({ address, symbol, rate }) => ({
            title: symbol,
            percentage: rate,
            address,
            blockchainId,
          }))
        : [],
    },
    lendingPosition,
    type: marketType,
    url: lendPages.market({ blockchainId, marketId: controller }),
    deprecatedMessage:
      DEPRECATED_LLAMAS[marketType][blockchainId]?.[controller]?.message ??
      lowSolvencyDeprecatedMessage(solvencyPercent),
    isFavorite: favoriteMarkets.has(vault),
    rewards,
    leverage: NO_LEVERAGE_LEND[blockchainId]?.includes(controller) ? null : leverage,
    userHasPositions:
      hasBorrowed || lendingPosition
        ? { [MarketRateType.Borrow]: !!hasBorrowed, [MarketRateType.Supply]: !!lendingPosition }
        : null,
    createdAt: new Date(createdAt).getTime(),
    favoriteKey: vault,
  }
}

/** We show WETH as ETH in the UI and market URL. Also change address so the symbol is correct */
const getCollateral = ({ address, symbol }: { address: Address; symbol: string }): [string, Address] =>
  symbol == 'WETH' ? ['ETH', ethAddress] : [symbol, address]

const convertMintMarket = (
  {
    address,
    collateralToken,
    collateralAmount,
    collateralAmountUsd,
    stablecoinToken,
    llamma,
    borrowApr,
    borrowed,
    borrowedUsd,
    borrowable,
    debtCeiling,
    loans,
    leverage,
    chain,
    maxLtv,
    monetaryPolicyAddress,
    oracle,
    priceOracle,
    ammPrice,
    ammA,
    loanDiscount,
    liquidationDiscount,
    minBand,
    maxBand,
    createdAt,
  }: MintMarket,
  favoriteMarkets: Set<Address>,
  campaigns: Record<string, CampaignRewards[]> = {},
  userMintMarkets: Set<Address> | undefined,
  collateralIndex: number, // index in the list of markets with the same collateral token, used to create a unique name
  badDebtUsd?: number,
): LlamaMarket => {
  const marketType = MarketType.Mint
  const borrowApy = aprToApy(borrowApr, 'llamalend.borrow')
  const hasBorrow = userMintMarkets?.has(address)
  const [collateralSymbol, collateralAddress] = getCollateral(collateralToken)
  const name = collateralIndex > 1 ? `${collateralSymbol}${collateralIndex}` : collateralSymbol
  const rewards = getMarketCampaigns(campaigns, {
    marketType,
    controllerAddress: address,
    vaultAddress: null,
    ammAddress: llamma,
  })
  const borrowCampaignsApr = sumCampaignsApr(rewards.filter(r => r.action === 'borrow'))
  const borrowCampaignsApy = sumCampaignsApy(rewards.filter(r => r.action === 'borrow'))
  const tvl = calculateMintMarketTvlUsd({ collateralAmountUsd })

  return {
    blockchainId: chain,
    controllerAddress: address,
    ammAddress: llamma,
    vaultAddress: null, // mint markets dont have these
    supplyGaugeAddress: null,
    // todo: the market's version should come from the backend directly like for the lend markets (not supported yet)
    version: [collateralSymbol, stablecoinToken.symbol].some(symbol => symbol.toLowerCase().includes('llv2'))
      ? MarketVersion.v2
      : MarketVersion.v1,
    assets: {
      borrowed: {
        symbol: stablecoinToken.symbol,
        address: stablecoinToken.address,
        decimals: stablecoinToken.decimals,
        blockchainId: chain,
        balance: borrowed,
        balanceUsd: borrowedUsd,
        rebasingYield: stablecoinToken.rebasingYield,
        rebasingYieldApr: stablecoinToken.rebasingYieldApr,
      },
      collateral: {
        symbol: collateralSymbol,
        address: collateralAddress,
        decimals: collateralToken.decimals,
        blockchainId: chain,
        balance: collateralAmount,
        balanceUsd: collateralAmountUsd,
        rebasingYield: collateralToken.rebasingYield,
        rebasingYieldApr: collateralToken.rebasingYieldApr,
      },
    },
    maxLtv,
    minBand,
    maxBand,
    loans,
    oraclePrice: priceOracle,
    ammPrice,
    monetaryPolicyAddress,
    oracleAddress: oracle,
    parameters: {
      A: ammA ?? null,
      loanDiscount: scaledFractionToPercent(loanDiscount),
      liquidationDiscount: scaledFractionToPercent(liquidationDiscount),
      adminFee: '0',
    },
    utilizationPercent: Math.min(100, (100 * borrowed) / debtCeiling), // debt ceiling may be lowered, so cap at 100%
    // solvency is only relevant for lending markets; if mint markets have bad debt that's a protocol problem, not a user problem
    solvencyPercent: null,
    badDebtUsd,
    debtCeiling,
    liquidity: borrowable,
    liquidityUsd: borrowable, // TODO: Use borrowableUsd instead of assuming crvUSD is worth $1.
    tvl,
    totalDebtUsd: borrowedUsd,
    totalCollateralUsd: tvl,
    rates: {
      lendApy: null,
      lendCrvAprBoosted: null,
      lendCrvAprUnboosted: null,
      lendTotalApyMinBoosted: null,
      lendTotalApyMaxBoosted: null,
      borrowApy,
      borrowTotalApy: computeTotalRate(borrowApy, collateralToken.rebasingYield ?? 0, borrowCampaignsApy ?? 0),
      borrowApr,
      borrowTotalApr: computeTotalRate(borrowApr, collateralToken.rebasingYieldApr ?? 0, borrowCampaignsApr ?? 0),
      incentives: [],
    },
    type: marketType,
    deprecatedMessage: DEPRECATED_LLAMAS[marketType][chain]?.[address]?.message ?? null,
    url: crvUsdPages.market({ blockchainId: chain, collateralId: name }),
    isFavorite: favoriteMarkets.has(llamma),
    rewards,
    leverage,
    userHasPositions: hasBorrow ? { [MarketRateType.Borrow]: hasBorrow, [MarketRateType.Supply]: false } : null,
    createdAt: new Date(createdAt).getTime(),
    favoriteKey: llamma,
  }
}

/**
 * Creates a function that counts the number of markets for each collateral token.
 * Numbers are added to the first items (e.g., first ETH gets 3, second gets 2, last gets 1).
 * @returns A function that takes a MintMarket and returns the count of markets for the collateral token.
 */
function createCountMarket(data: MintMarket[] | undefined = []) {
  const getName = ({ collateralToken }: MintMarket) => getCollateral(collateralToken)[0]
  const marketCountByCollateral = countBy(data, getName)
  return (m: MintMarket) => marketCountByCollateral[getName(m)]--
}

type LlamaMarketsQueries = [
  ReturnType<typeof getLendingVaultsOptions>,
  ReturnType<typeof getMintMarketOptions>,
  ReturnType<typeof getBadDebtLendMarketsOptions>,
  ReturnType<typeof getBadDebtMintMarketsOptions>,
  ReturnType<typeof getCampaignsExternalOptions>,
  ReturnType<typeof getCampaignsMarketsMerklOptions>,
  ReturnType<typeof getFavoriteMarketOptions>,
]

export type LlamaMarketParams = { userAddress: Address | undefined; enableDeprecatedMarkets: boolean }

/**
 * Query hook combining all lend and mint markets of all chains into a single list, converting them to a common format.
 * It also fetches the user's favorite markets and user's positions list (without the details).
 */
export const useLlamaMarkets = ({ userAddress, enableDeprecatedMarkets }: LlamaMarketParams, enabled = true) => {
  const userPositions = useUserLlamaPositions({ userAddress }, enabled)
  return useQueries({
    queries: useMemo<LlamaMarketsQueries>(
      () => [
        getLendingVaultsOptions({}, enabled),
        getMintMarketOptions({}, enabled),
        getBadDebtLendMarketsOptions(enabled),
        getBadDebtMintMarketsOptions(enabled),
        getCampaignsExternalOptions(getAddress, enabled),
        getCampaignsMarketsMerklOptions(getAddress, enabled),
        getFavoriteMarketOptions({}, enabled),
      ],
      [enabled],
    ),
    combine: useCallback(
      (results: QueriesResults<LlamaMarketsQueries>): Query<LlamaMarketsResult> => {
        if (!enabled) return DISABLED_Q // used in the header, only run when llamalend is selected
        const [
          lendingVaults,
          mintMarkets,
          badDebtLendMarkets,
          badDebtMintMarkets,
          externalCampaigns,
          merklCampaigns,
          favoriteMarkets,
        ] = results
        const favoriteMarketsSet = new Set(favoriteMarkets.data)
        const { userBorrows, userMints, userSuppliesByChain, userHasPositions = null } = userPositions.data ?? {}
        const countMarket = createCountMarket(mintMarkets.data)
        const campaigns = combineCampaigns([externalCampaigns.data, merklCampaigns.data])
        const getLendMarketBadDebt = createGetBadDebtMarket(badDebtLendMarkets.data)
        const getMintMarketBadDebt = createGetBadDebtMarket(badDebtMintMarkets.data)

        const marketsReady = [lendingVaults, mintMarkets].every(q => q.data ?? q.error)

        const data: LlamaMarketsResult | undefined = marketsReady
          ? {
              userHasPositions,
              hasFavorites: favoriteMarketsSet.size > 0,
              markets: [
                ...(lendingVaults.data ?? [])
                  .filter(({ createdAt, version }) => version != 1 || createdAt <= LEND_V1_DEPRECATION_DATE.getTime())
                  .map(vault =>
                    convertLendingVault(
                      vault,
                      favoriteMarketsSet,
                      campaigns,
                      userBorrows?.[vault.chain],
                      userSuppliesByChain?.[vault.chain]?.[vault.vault],
                      getLendMarketBadDebt(vault.chain, vault.controller),
                    ),
                  ),
                ...(mintMarkets.data ?? []).map(market =>
                  convertMintMarket(
                    market,
                    favoriteMarketsSet,
                    campaigns,
                    userMints?.[market.chain],
                    countMarket(market),
                    getMintMarketBadDebt(market.chain, market.address),
                  ),
                ),
              ].filter(
                ({ deprecatedMessage, userHasPositions }) =>
                  !deprecatedMessage || enableDeprecatedMarkets || userHasPositions,
              ),
            }
          : undefined
        return { data, ...combineQueryState(userPositions, ...results) }
      },
      [enabled, enableDeprecatedMarkets, userPositions],
    ),
  })
}
